/**
 * Dental Stage 2: Discharge Information — PDF component and field definitions.
 *
 * Auto-filled: clinic header, patient name throughout.
 * Manual fields: teeth removed, medication lines, follow-up dates.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'
import { addDays } from 'date-fns'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import {
  formatCalendarDate,
  resolveDateFormat,
} from '@/frontend/features/printing/documents/blocks/format-utils'
import { InlineField } from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import {
  type DischargeMedicationEntry,
  dentalClosingText,
  MEDICATION_SIDE_EFFECTS,
  medicationText,
  OUT_OF_HOURS,
  THINGS_TO_NOTICE,
} from '@/frontend/features/printing/documents/discharge/shared'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

export interface DentalDischargeStage2Fields {
  teethRemoved: string
  medications: DischargeMedicationEntry[]
  followUpDate1: Date | null
  followUpDate2: Date | null
}

const today = () => new Date()

export const defaultDentalDischargeStage2Fields: DentalDischargeStage2Fields = {
  teethRemoved: '',
  medications: [],
  followUpDate1: addDays(today(), 3),
  followUpDate2: addDays(today(), 10),
}

export function procedureText(patientName: string) {
  return `${patientName} has had a general anaesthetic for a dental procedure. We removed the teeth that showed abnormalities and have taken follow up X-rays.`
}

export function teethRemovedText(patientName: string) {
  return `You are provided with ${patientName}'s dental chart to clarify which teeth have been removed.`
}

/**
 * The stage 2 example included an extra "Limit physical activity and play" sentence
 * not present in the generic rest clause, so we've defined it separately for now.
 * This may be an oversight or human variance — there could be opportunity to align in the future.
 */
export function restText(patientName: string) {
  return `Following ${patientName}'s procedure today, expect them to feel a bit drowsy for the next 24-48 hours while the medications given today have time to fully metabolise. Allow ${patientName} to rest in a quiet and comfortable area away from other pets for the next 12 hours. Limit physical activity and play during this time.`
}

export function feedingText(patientName: string) {
  return `${patientName} has eaten with us today. When you go home today, offer ${patientName} a bland, soft diet such as boiled chicken or a commercial diet such as Royal Canin Gastrointestinal. We recommend offering small portions at a time as sometimes the anaesthetic drugs can cause nausea. Continue feeding a soft food diet for the next 7-10 days.`
}

export const FEEDING_EXTRAS = [
  'Avoid giving hard treats until the sites heal completely.',
  'Provide fresh water at all times.',
]

export function surgicalCareText(patientName: string) {
  return `Check ${patientName}'s mouth daily for any signs of swelling, redness, foul smells or unusual discharge. It is normal for there to be some blood mixed with saliva for up to 48 hours post procedure, however if you notice profuse bleeding or are concerned, please get in contact with the clinic.`
}

export const SURGICAL_CARE_EXTRA = 'Avoid brushing your pets teeth for at least 10 days.'

export const FOLLOW_UP_TEXT =
  'A recheck appointment is recommended in 3 days and in 10 days post procedure.'

const styles = StyleSheet.create({
  ...baseStyles,
})

export function DentalDischargeStage2Pdf({
  context,
  fields,
}: {
  context: DocumentContext
  fields: DentalDischargeStage2Fields
}) {
  const { patient, owner, clinic } = context
  const name = patient.name
  const meds = fields.medications.filter((m) => m.name)
  // Generated for the owner, not a viewer, so the date order follows their region.
  const fmt = resolveDateFormat(null, owner.region)

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.heading}>Dental Stage 2: Discharge Information</Text>

        <Text style={styles.text}>
          <Text style={styles.bold}>Procedure: </Text>
          {procedureText(name)}
        </Text>

        <View
          style={{
            ...styles.sectionGap,
            flexDirection: 'row',
            alignItems: 'baseline',
            gap: mm(2),
            flexWrap: 'wrap',
          }}
        >
          <Text style={styles.text}>We have removed</Text>
          <InlineField value={fields.teethRemoved} />
          <Text style={styles.text}>teeth. {teethRemovedText(name)}</Text>
        </View>

        <Text style={styles.heading}>Rest and activity:</Text>
        <Text style={styles.text}>{restText(name)}</Text>

        <Text style={styles.heading}>Feeding:</Text>
        <Text style={styles.text}>{feedingText(name)}</Text>
        {FEEDING_EXTRAS.map((item) => (
          <Text key={item} style={styles.text}>
            {item}
          </Text>
        ))}

        <Text style={styles.heading}>Medication:</Text>
        <Text style={styles.text}>{medicationText(name)}</Text>
        <Text style={styles.text}>Medication to go home with are as follows;</Text>
        {meds.length > 0 ? (
          meds.map((med) => (
            <Text key={med.key} style={styles.listItem}>
              <Text style={styles.bold}>• {med.name}</Text>
              {med.instructions ? `  — ${med.instructions}` : ''}
            </Text>
          ))
        ) : (
          <Text style={styles.text}>None specified</Text>
        )}
        <Text style={styles.text}>{MEDICATION_SIDE_EFFECTS}</Text>

        <Text style={styles.heading}>Surgical site care:</Text>
        <Text style={styles.text}>{surgicalCareText(name)}</Text>
        <Text style={styles.text}>{SURGICAL_CARE_EXTRA}</Text>

        <Text style={styles.heading}>Follow up appointments:</Text>
        <Text style={styles.text}>{FOLLOW_UP_TEXT}</Text>
        <Text style={{ ...styles.text, ...styles.bold }}>Follow up appointment dates:</Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: mm(2) }}>
          <InlineField
            value={
              fields.followUpDate1
                ? // Days picked in a date picker, not moments — no zone may shift them.
                  formatCalendarDate(fields.followUpDate1, fmt, 'long')
                : undefined
            }
          />
          <Text style={styles.text}>&</Text>
          <InlineField
            value={
              fields.followUpDate2
                ? formatCalendarDate(fields.followUpDate2, fmt, 'long')
                : undefined
            }
          />
        </View>

        <Text style={styles.heading}>Other things you may notice:</Text>
        {THINGS_TO_NOTICE.map((item) => (
          <Text key={item.slice(0, 30)} style={styles.listItem}>
            • {item}
          </Text>
        ))}

        <Text style={styles.heading}>Out of hours contact:</Text>
        <Text style={styles.text}>{OUT_OF_HOURS}</Text>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>{dentalClosingText(name)}</Text>
          <Text style={styles.text}>Snoots Vet, North London.</Text>
        </View>
      </Page>
    </Document>
  )
}
